import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Search, ChevronRight, FileText } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { getPatientName, formatDate } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

export function ConfirmedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['approvals', 'confirmed', user?.hospital_id, page],
    queryFn: () =>
      api.approvals.confirmed({
        hospitalId: user?.hospital_id ?? undefined,
        page,
        pageSize: 20,
      }),
  });

  const items = data?.items ?? [];

  const filtered = items.filter((item) => {
    if (search) {
      const q = search.toLowerCase();
      const name = getPatientName(item.patient_info).toLowerCase();
      const ref = item.ihalaty_reference.toLowerCase();
      return name.includes(q) || ref.includes(q);
    }
    return true;
  });

  return (
    <div className="px-5 py-5 lg:px-8 lg:py-8 max-w-lg lg:max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
          <CheckCircle className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">
            Confirmed
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Confirmed transfer cases
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or reference..."
          className="pl-9"
        />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] rounded-2xl" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center gap-3 p-3.5 rounded-2xl bg-card border border-border hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => navigate(`/confirmed/${item.id}?from=/confirmed`)}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-primary-50 text-primary">
                <CheckCircle className="w-4 h-4" strokeWidth={2.5} />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {getPatientName(item.patient_info)}
                </p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {item.ihalaty_reference}
                  </span>
                  {item.gms_referral_id && (
                    <span className="text-[11px] font-mono text-blue-600">
                      GMS-{item.gms_referral_id}
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground">
                    {item.decided_at ? formatDate(item.decided_at) : ''}
                  </span>
                </div>
              </div>

              {item.acceptance_letter && (
                <a
                  href={api.approvals.getAttachmentUrl(
                    item.acceptance_letter.id,
                    item.acceptance_letter.filename,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 rounded-lg hover:bg-muted transition-colors shrink-0"
                  title="Download acceptance letter"
                >
                  <FileText className="w-4 h-4 text-primary" />
                </a>
              )}

              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="py-16 text-center">
          <p className="text-muted-foreground text-sm">
            {search ? 'No matching results' : 'No confirmed cases yet'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-3">
            {page} / {data.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
